import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { BankAccountFormDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/BankAccountFormDrawer.tsx';
import { useBankAccountDrawer } from '@dealstack/frontend/shared/components/BankAccountDrawer/useBankAccountDrawer.ts';
import type { BankAccountDetails } from '@dealstack/frontend/shared/components/BankAccountForm/types.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	hasPermission: boolean;
	opened: boolean;
	onClose: () => void;
	/** null adds a new account; an account edits it. */
	bankAccount: BankAccountDetails | null;
	/** Person the account hangs off; every request the drawer makes is scoped to them. */
	personId: string;
	/** Country pre-selected for a new account, usually inferred from the person's address. */
	inferredCountryCode?: string;
	/** Run once the drawer closes, so the host can refresh what it renders. */
	onSaved: () => void;
}

/** Add/edit drawer for a person's bank account, wired to the person-scoped endpoints. */
export const BankAccountDrawer = ({
	bankAccount,
	hasPermission,
	opened,
	onClose,
	personId,
	inferredCountryCode,
	onSaved,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { verifyIdentifiers, isRequestLoading, onFormSubmit, closeDrawer } = useBankAccountDrawer(
		{
			personId,
			bankAccount,
			onClose,
			onSaved,
		}
	);

	return (
		<BankAccountFormDrawer
			opened={opened}
			onClose={closeDrawer}
			title={
				bankAccount
					? tCapManage('bank_account.drawer.edit_title')
					: tCapManage('bank_account.drawer.add_title')
			}
			headingTestId={TEST_ELEMENT_IDS.CAP_BANK_ACCOUNT_DRAWER_HEADING}
			hasPermission={hasPermission}
			bankAccount={bankAccount}
			isRequestLoading={isRequestLoading}
			onSubmit={onFormSubmit}
			verifyIdentifiers={verifyIdentifiers}
			inferredCountryCode={inferredCountryCode}
		/>
	);
};
